import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api, { money } from "@/lib/api";
import RoleShell from "@/components/RoleShell";
import { Stat, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { LayoutDashboard, Store, Package, ClipboardList, Plus, Pencil, Trash2, TrendingUp, ShoppingBag, Percent, Wallet } from "lucide-react";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "store", label: "Store", icon: Store },
  { key: "products", label: "Products", icon: Package },
  { key: "orders", label: "Orders", icon: ClipboardList },
];

const CITY_COORDS = { Istanbul: { lat: 41.02, lng: 28.98 }, Ankara: { lat: 39.93, lng: 32.86 }, Izmir: { lat: 38.42, lng: 27.14 } };
const emptyProduct = { name: "", description: "", category: "", price: 0, stock: 0, weight_kg: 1, length_cm: 10, width_cm: 10, height_cm: 10, unit: "piece", images: [], available: true };

export default function MerchantApp() {
  const { t } = useTranslation();
  const [active, setActive] = useState("dashboard");
  const [store, setStore] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cats, setCats] = useState([]);

  const loadAll = useCallback(async () => {
    const [s, a, p, o, c] = await Promise.all([
      api.get("/merchant/store"), api.get("/merchant/analytics"), api.get("/merchant/products"),
      api.get("/orders"), api.get("/categories"),
    ]);
    setStore(s.data); setAnalytics(a.data); setProducts(p.data); setOrders(o.data); setCats(c.data);
  }, []);
  useEffect(() => { loadAll(); const t = setInterval(() => { api.get("/orders").then((r) => setOrders(r.data)); }, 6000); return () => clearInterval(t); }, [loadAll]);

  return (
    <RoleShell title={t("roles.merchant")} tabs={TABS} active={active} setActive={setActive}
      right={store && <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground">{t("merchant.storeLabel")}</span><Switch data-testid="store-open-toggle" checked={!!store.is_open} onCheckedChange={async (v) => { await api.post("/merchant/store/status", { is_open: v }); setStore({ ...store, is_open: v }); }} /><span>{store.is_open ? t("common.open") : t("common.closed")}</span></div>}>
      {active === "dashboard" && <Dashboard analytics={analytics} store={store} />}
      {active === "store" && <StoreForm store={store} cats={cats} onSaved={loadAll} />}
      {active === "products" && <Products products={products} cats={cats} store={store} reload={loadAll} />}
      {active === "orders" && <Orders orders={orders} reload={loadAll} />}
    </RoleShell>
  );
}

function Dashboard({ analytics, store }) {
  const { t } = useTranslation();
  if (!analytics) return null;
  return (
    <div className="space-y-6">
      {!store && <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 p-4 text-sm">{t("merchant.noStoreWarn")}</div>}
      {store && store.status !== "approved" && <div className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-800 p-4 text-sm">{t("merchant.statusWarn", { status: store.status })}</div>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={t("roles.orders")} value={analytics.orders} icon={ShoppingBag} />
        <Stat label={t("merchant.totalSales")} value={money(analytics.total_sales, store?.currency)} icon={TrendingUp} />
        <Stat label={t("merchant.commission")} value={money(analytics.commission, store?.currency)} icon={Percent} accent="text-rose-500" />
        <Stat label={t("merchant.netEarnings")} value={money(analytics.net_earnings, store?.currency)} icon={Wallet} accent="text-emerald-500" />
      </div>
      <div className="rounded-2xl border bg-card p-5">
        <div className="font-semibold mb-4">{t("merchant.bestSelling")}</div>
        {analytics.best_products.length === 0 ? <div className="text-sm text-muted-foreground">{t("merchant.noSales")}</div> :
          analytics.best_products.map((b, i) => (
            <div key={i} className="flex justify-between py-2 border-b last:border-0 text-sm"><span>{b.name}</span><span className="font-semibold">{b.qty} {t("merchant.sold")}</span></div>
          ))}
      </div>
    </div>
  );
}

function StoreForm({ store, cats, onSaved }) {
  const { t } = useTranslation();
  const [f, setF] = useState(store || { name: "", description: "", category: "", city: "Istanbul", cover: "", ...CITY_COORDS.Istanbul });
  useEffect(() => { if (store) setF(store); }, [store]);
  const save = async () => {
    try { await api.post("/merchant/store", { ...f, ...CITY_COORDS[f.city] }); toast.success(t("merchant.storeSaved")); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || t("common.saveFailed")); }
  };
  return (
    <div className="max-w-2xl rounded-2xl border bg-card p-5 space-y-4">
      <div><Label>{t("merchant.storeName")}</Label><Input data-testid="store-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 rounded-xl" /></div>
      <div><Label>{t("form.description")}</Label><Textarea data-testid="store-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 rounded-xl" /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>{t("form.category")}</Label>
          <select data-testid="store-cat" value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-xl border bg-card text-sm">
            <option value="">{t("form.select")}</option>{cats.map((c) => <option key={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div><Label>{t("form.city")}</Label>
          <select data-testid="store-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-xl border bg-card text-sm">
            {Object.keys(CITY_COORDS).map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div><Label>{t("form.cover")}</Label><Input data-testid="store-cover" value={f.cover || ""} onChange={(e) => setF({ ...f, cover: e.target.value })} className="mt-1 rounded-xl" placeholder="https://…" /></div>
      <Button data-testid="save-store" onClick={save} className="rounded-full bg-brand hover:bg-brand-dark">{t("merchant.saveStore")}</Button>
    </div>
  );
}

function ProductDialog({ trigger, initial, cats, onSave }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initial);
  useEffect(() => { setF(initial); }, [initial, open]);
  const num = (k) => (e) => setF({ ...f, [k]: parseFloat(e.target.value) || 0 });
  const save = async () => { await onSave({ ...f, images: f.images_str ? [f.images_str] : (f.images || []) }); setOpen(false); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{initial.id ? t("merchant.editProduct") : t("merchant.newProduct")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t("form.name")}</Label><Input data-testid="p-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 rounded-xl" /></div>
          <div><Label>{t("form.description")}</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1 rounded-xl" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("form.category")}</Label>
              <select value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-xl border bg-card text-sm">
                <option value="">{t("form.select")}</option>{cats.map((c) => <option key={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><Label>{t("form.unit")}</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><Label>{t("form.price")}</Label><Input data-testid="p-price" type="number" value={f.price} onChange={num("price")} className="mt-1 rounded-xl" /></div>
            <div><Label>{t("form.stock")}</Label><Input type="number" value={f.stock} onChange={num("stock")} className="mt-1 rounded-xl" /></div>
            <div><Label>{t("form.weight")} (kg)</Label><Input data-testid="p-weight" type="number" value={f.weight_kg} onChange={num("weight_kg")} className="mt-1 rounded-xl" /></div>
            <div className="grid grid-cols-3 gap-1 col-span-1">
              <div><Label className="text-[10px]">{t("form.lengthCm")}</Label><Input type="number" value={f.length_cm} onChange={num("length_cm")} className="mt-1 rounded-lg px-2" /></div>
              <div><Label className="text-[10px]">{t("form.widthCm")}</Label><Input type="number" value={f.width_cm} onChange={num("width_cm")} className="mt-1 rounded-lg px-2" /></div>
              <div><Label className="text-[10px]">{t("form.heightCm")}</Label><Input type="number" value={f.height_cm} onChange={num("height_cm")} className="mt-1 rounded-lg px-2" /></div>
            </div>
          </div>
          <div><Label>{t("form.image")}</Label><Input value={f.images_str ?? (f.images?.[0] || "")} onChange={(e) => setF({ ...f, images_str: e.target.value })} className="mt-1 rounded-xl" placeholder="https://…" /></div>
        </div>
        <DialogFooter><Button data-testid="save-product" onClick={save} className="rounded-full bg-brand hover:bg-brand-dark">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Products({ products, cats, store, reload }) {
  const { t } = useTranslation();
  const create = async (p) => { try { await api.post("/merchant/products", p); toast.success(t("merchant.created")); reload(); } catch (e) { toast.error(e.response?.data?.detail || t("common.actionFailed")); } };
  const edit = async (id, p) => { await api.put(`/merchant/products/${id}`, p); toast.success(t("merchant.updated")); reload(); };
  const del = async (id) => { await api.delete(`/merchant/products/${id}`); toast.success(t("merchant.deleted")); reload(); };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">{t("merchant.productsCount", { count: products.length })}</div>
        {store && <ProductDialog trigger={<Button data-testid="new-product-btn" className="rounded-full bg-brand hover:bg-brand-dark gap-2"><Plus className="w-4 h-4" /> {t("merchant.newProduct")}</Button>} initial={emptyProduct} cats={cats} onSave={create} />}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`m-product-${p.id}`} className="rounded-2xl border bg-card overflow-hidden">
            <div className="aspect-video bg-muted overflow-hidden">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <div className="font-semibold text-sm">{p.name}</div>
              <div className="text-brand font-bold mt-1">{money(p.price, p.currency)}</div>
              <div className="text-xs text-muted-foreground mt-1">{t("merchant.stockLabel")}: {p.stock} · {p.weight_kg}kg</div>
              <div className="flex gap-2 mt-3">
                <ProductDialog trigger={<Button size="sm" variant="outline" className="rounded-lg gap-1 flex-1"><Pencil className="w-3 h-3" /> {t("common.edit")}</Button>} initial={p} cats={cats} onSave={(np) => edit(p.id, np)} />
                <Button size="icon" variant="outline" className="rounded-lg text-destructive" onClick={() => del(p.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ACTIONS = {
  PENDING: [["accept", "merchant.accept", "bg-brand hover:bg-brand-dark"], ["reject", "merchant.reject", "bg-destructive hover:bg-destructive/90"]],
  CONFIRMED: [["prepare", "merchant.startPreparing", "bg-brand hover:bg-brand-dark"]],
  PREPARING: [["ready", "merchant.markReady", "bg-brand hover:bg-brand-dark"]],
};
function Orders({ orders, reload }) {
  const { t } = useTranslation();
  const act = async (id, action) => { await api.post(`/merchant/orders/${id}/${action}`); toast.success(t("merchant.orderUpdated")); reload(); };
  if (orders.length === 0) return <div className="text-center py-16 text-muted-foreground">{t("merchant.noOrders")}</div>;
  return (
    <div className="space-y-3">
      {orders.map((o) => (
        <div key={o.id} data-testid={`m-order-${o.id}`} className="rounded-2xl border bg-card p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div><div className="font-semibold">{o.customer_name} · <span className="font-mono text-xs text-muted-foreground">#{o.id.slice(-8)}</span></div>
              <div className="text-xs text-muted-foreground mt-0.5">{o.items.length} · {money(o.total, o.currency)} · {o.address?.city} · {t(`vehicle.${o.recommended_vehicle}`, o.recommended_vehicle)}</div></div>
            <StatusBadge status={o.status} />
          </div>
          <div className="text-sm text-muted-foreground mt-2">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
          {(ACTIONS[o.status] || []).length > 0 && (
            <div className="flex gap-2 mt-3">
              {ACTIONS[o.status].map(([a, key, cls]) => (
                <Button key={a} data-testid={`order-${a}-${o.id}`} size="sm" onClick={() => act(o.id, a)} className={`rounded-full ${cls}`}>{t(key)}</Button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
